<template>
  <q-page class="instruments-page">
    <div class="instruments-container">
      <!-- Loading state -->
      <div v-if="loading" class="instruments-container">
        <div>
          <q-skeleton type="text" width="260px" dark style="font-size: 1.5rem;" />
          <q-skeleton type="text" width="360px" dark style="margin-top: 4px;" />
        </div>
        <div class="instruments-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 4" :key="n" type="rect" height="160px" dark style="border-radius: 16px;" />
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" style="text-align: center; padding: 48px 16px;">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p style="color: var(--oa-text-muted); margin-top: 12px;">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded -->
      <template v-else>
        <!-- Back link -->
        <router-link to="/evaluations" class="back-link">
          <q-icon name="arrow_back" size="14px" />
          Evaluaciones
        </router-link>

        <!-- Header -->
        <h1 class="page-title">Instrumentos de Evaluacion</h1>
        <p class="page-subtitle">
          Selecciona un instrumento para iniciar una evaluacion de supervision.
        </p>

        <!-- Instruments grid -->
        <div class="instruments-grid">
          <router-link
            v-for="inst in instrumentList"
            :key="inst.key"
            :to="`/evaluations/live/new?instrument=${inst.key}`"
            class="instrument-card"
          >
            <!-- Icon + info -->
            <div class="card-top">
              <div class="card-icon-wrap">
                <q-icon :name="inst.icon" size="24px" />
              </div>
              <div class="card-info">
                <h3 class="card-label">{{ inst.label }}</h3>
                <p class="card-description">{{ inst.description }}</p>
              </div>
            </div>

            <!-- Stats -->
            <div class="card-stats">
              <q-badge outline :label="inst.type" class="card-type-badge" />
              <span class="card-stat">{{ instrumentStats[inst.key]?.areas ?? 0 }} areas</span>
              <span class="card-stat">{{ instrumentStats[inst.key]?.criteria ?? 0 }} criterios</span>
            </div>

            <!-- Weight -->
            <div class="card-bottom">
              <span class="card-weight">Peso: {{ inst.weight }}</span>
              <q-btn
                no-caps
                unelevated
                dense
                label="Iniciar evaluacion"
                class="card-start-btn"
                @click.prevent
              />
            </div>
          </router-link>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from 'boot/axios';

interface InstrumentArea {
  id: string;
  name: string;
  weight: string;
  criteria: { id: string }[];
}

const INSTRUMENT_INFO = [
  {
    key: 'live_observation',
    label: 'Observacion en Vivo',
    description: 'Evalua al entrenador durante una clase real. Escala 1-5 por criterio.',
    icon: 'visibility',
    type: 'Escala 1-5',
    weight: '35%',
  },
  {
    key: 'performance_binary',
    label: 'Desempeno Operativo',
    description: 'Checklist binario de desempeno operativo. Si/No por item.',
    icon: 'fact_check',
    type: 'Binario',
    weight: '25%',
  },
  {
    key: 'protocol_checklist',
    label: 'Adherencia a Protocolos',
    description: 'Verifica cumplimiento de protocolos de apertura, clase, seguridad y cierre.',
    icon: 'verified',
    type: 'Binario',
    weight: '20%',
  },
  {
    key: 'routine_execution',
    label: 'Ejecucion de Rutinas',
    description: 'Evalua las rutinas diarias del entrenador: inicio, entrenamiento, cierre, post.',
    icon: 'list_alt',
    type: 'Binario',
    weight: '20%',
  },
];

const loading = ref(true);
const error = ref<string | null>(null);
const instrumentStats = ref<Record<string, { areas: number; criteria: number }>>({});
const instrumentList = INSTRUMENT_INFO;

async function fetchData() {
  loading.value = true;
  error.value = null;

  try {
    const { data } = await api.get<Record<string, InstrumentArea[]>>('/supervision/instruments');

    const stats: Record<string, { areas: number; criteria: number }> = {};
    for (const [key, areas] of Object.entries(data ?? {})) {
      const areaList = areas as InstrumentArea[];
      stats[key] = {
        areas: areaList.length,
        criteria: areaList.reduce((s, a) => s + (a.criteria?.length ?? 0), 0),
      };
    }
    instrumentStats.value = stats;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error al cargar instrumentos';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchData);
</script>

<style lang="scss" scoped>
.instruments-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.instruments-container {
  max-width: 960px;
  width: 100%;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--oa-gold-full);
  text-decoration: none;
  margin-bottom: 16px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--oa-text-muted);
  font-size: 14px;
  margin: 4px 0 0;
}

// --- Grid ---
.instruments-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 24px;
}

@media (min-width: 768px) {
  .instruments-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

// --- Card ---
.instrument-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  text-decoration: none;
  color: inherit;
  transition: all 0.2s var(--oa-ease);

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 20%, var(--oa-border));
    box-shadow: 0 0 16px rgba(232, 184, 75, 0.06);
  }

  &:hover .card-label {
    color: var(--oa-gold-full);
  }
}

.card-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.card-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
  flex-shrink: 0;
}

.card-info {
  flex: 1;
  min-width: 0;
}

.card-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
  transition: color 0.15s;
}

.card-description {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
  line-height: 1.4;
}

.card-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-type-badge {
  font-size: 10px;
}

.card-stat {
  font-size: 12px;
  color: var(--oa-text-disabled);
}

.card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-weight {
  font-size: 12px;
  font-weight: 600;
  color: var(--oa-text-secondary);
}

.card-start-btn {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  font-size: 12px;
  border-radius: 8px;
  padding: 6px 16px;
}
</style>
